import moment from 'moment';
import URI from 'urijs';

import * as E from 'fp-ts/Either';
import * as TE from 'fp-ts/TaskEither';

import { LocalDateTime } from '@/shared/modules/date';
import { getLangFromCookies } from '@/utils/cookies';

import fetchUrl, { FetchError, fetchJson, postJson } from '../../utils/fetch';
import { BusinessWorker } from '../booking/types';
import { SerializedBooking } from '../booking/utils';
import { Api, BVScheduledFlight, BVScheduledTransport } from './model';
import { ApiUtils } from './utils';

const displayDate = (date: Date) => moment(date).format('DD/MM/YYYY');

const matchTripNumber = [
  { start: 'EJU', replace: 'EZY' },
  { start: 'EZS', replace: 'EZY' },
  { start: 'CE', replace: 'CE0' },
  { start: 'EK', replace: 'EK0' },
  { start: 'I2', replace: 'IB' },
];

export default class API {
  static getSites(): Promise<Api.Referential> {
    const lang = getLangFromCookies();

    return fetchJson<Api.Referential>(`/sites.json?lang=${lang}`);
  }

  static getSiteDepartureSlots(site: string, date: Date, orderId?: string) {
    const orderIdPart = orderId ? `&order_id=${orderId}` : '';
    return fetchJson<Api.SiteSlots>(`/sites/slots/departure.json?site=${site}&date=${displayDate(date)}${orderIdPart}`);
  }

  static getSiteArrivalSlots(site: string, date: Date, orderId?: string) {
    const orderIdPart = orderId ? `&order_id=${orderId}` : '';
    return fetchJson<Api.SiteSlots>(`/sites/slots/arrival.json?site=${site}&date=${displayDate(date)}${orderIdPart}`);
  }

  static getServices(
    startSite: string | undefined,
    endSite: string | undefined,
    startDate: string | null,
    endDate: string | null,
    business: string | undefined,
    worker: BusinessWorker | undefined,
  ) {
    const datePart = startDate && endDate ? `&start_date=${startDate}&end_date=${endDate}` : '';

    const businessPart = business ? `&business_id=${business}` : '';
    const workerPart = worker ? `&worker_id=${worker.id}` : '';

    return fetchJson<Array<Api.Service>>(
      `/services.json?start_site=${startSite}&end_site=${endSite}${datePart}${businessPart}${workerPart}`,
    );
  }

  static getCompanies() {
    return fetchUrl('/business/companies.json', { method: 'GET', headers: { Accept: 'application/json' } });
  }

  static getSummary(): TE.TaskEither<FetchError, Response> {
    const fetch = () => fetchUrl('/cart/summary.json', { method: 'GET', headers: { Accept: 'application/json' } });
    return TE.tryCatch(fetch, ApiUtils.handleFetchError);
  }

  static async getDetails() {
    return fetchUrl('/cart/details.json', { method: 'GET', headers: { Accept: 'application/json' } });
  }

  static estimate(start: SerializedBooking, end: SerializedBooking): Promise<Api.FastBookingEstimation> {
    return postJson('/orders/estimate', { ...start, ...end });
  }

  static saveCart(body: string): TE.TaskEither<FetchError, Response> {
    const fetch = () => fetchUrl('/cart', { method: 'PUT', body, headers: { 'Content-Type': 'application/json' } });
    return TE.tryCatch(fetch, ApiUtils.handleFetchError);
  }

  static getQuery(prefix: string, site: string | number, date: string, query: string) {
    const url = new URI(`/transport/${prefix}.json`);
    // Ajout du site
    url.addQuery('site', site);

    // Ajout de la date
    url.addQuery('date', date);

    // Ajout du numéro de vol / train
    let tempQuery = query;
    if (tempQuery) {
      tempQuery = tempQuery.toUpperCase();
      const findReplace = matchTripNumber.find(match => tempQuery.startsWith(match.start));
      if (findReplace && !tempQuery.match(findReplace.replace)) {
        tempQuery = tempQuery.replace(findReplace.start, findReplace.replace);
      }
    }

    url.addQuery('query', tempQuery);

    return url;
  }

  static getFlightsArriving(
    siteId: string | number,
    dateTime: LocalDateTime,
    signal?: AbortSignal,
  ): TE.TaskEither<Error, Array<BVScheduledFlight>> {
    const url = new URI('/transport/flights/arriving.json');
    url.addQuery('siteId', siteId);
    url.addQuery('dateTime', dateTime);
    return TE.tryCatch(() => fetchJson<Array<BVScheduledFlight>>(url.toString(), { method: 'GET', signal }), E.toError);
  }

  static getTripInfos(
    prefix: 'departure' | 'arrival',
    site: string | number,
    date: string,
    query: string,
    signal?: AbortSignal,
  ): Promise<BVScheduledTransport> {
    const uri = this.getQuery(prefix, site, date, query);

    return fetchJson(uri.toString(), { method: 'GET', signal });
  }

  static simulateUpdate(orderId: string, form: any) {
    return postJson(`/orders/${orderId}/simulate`, form);
  }
}
